import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CtaBand } from "../components/CtaBand";
import { WoodSwatch } from "../components/WoodSwatch";
import { API_URL, PRODUCT_FEATURES } from "../config";

const ColourCard = ({ c, i, onRequest }) => (
  <article
    className="group border border-[#E5E5E5] rounded-xl overflow-hidden bg-white transition-all duration-300 hover:shadow-lg hover:-translate-y-1 fade-up"
    style={{ animationDelay: `${(i % 8) * 60}ms` }}
    data-testid={`colour-card-${c.code}`}
  >
    <div className="aspect-[4/3] overflow-hidden">
      {c.image_url ? (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${c.image_url}`}
          alt={`${c.name} ${c.thickness || "7.5 mm"} hybrid flooring`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      ) : (
        <WoodSwatch light={c.light} dark={c.dark} seed={i + 3} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
      )}
    </div>
    <div className="p-5">
      <h3 className="text-lg font-semibold font-heading" data-testid={`colour-name-${c.code}`}>{c.name}</h3>
      <p className="text-xs text-[#525252] mt-0.5">Product code: {c.code}</p>
      <ul className="mt-3 flex flex-wrap gap-1.5">
        {[c.thickness || "7.5 mm", "SPC core", "Attached underlay"].map((tag) => (
          <li key={tag} className="text-xs bg-[#FAFAFA] border border-[#E5E5E5] rounded-full px-2.5 py-1 text-[#525252]">
            {tag}
          </li>
        ))}
      </ul>
      <ul className="mt-4 space-y-1.5" data-testid={`colour-features-${c.code}`}>
        {PRODUCT_FEATURES.map((f) => (
          <li key={f} className="flex items-center gap-2 text-xs text-[#525252]">
            <Check className="w-3.5 h-3.5 text-[#C87A38] shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={() => onRequest(c.style === "herringbone" ? `${c.name} (Herringbone)` : c.name)}
        className="mt-4 w-full rounded-full border border-[#C87A38] text-[#C87A38] px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-[#C87A38] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#C87A38]"
        data-testid={`request-colour-${c.code}`}
      >
        Request This Colour
      </button>
    </div>
  </article>
);

const ColourSection = ({ label, title, description, colours, onRequest, testId }) => (
  <section className="py-16 first:pt-20" data-testid={testId}>
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C87A38]">{label}</p>
      <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-semibold">{title}</h2>
      <p className="mt-3 text-base text-[#525252] max-w-2xl leading-relaxed">{description}</p>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {colours.map((c, i) => (
          <ColourCard key={c.id} c={c} i={i} onRequest={onRequest} />
        ))}
      </div>
    </div>
  </section>
);

export default function Colours() {
  const [colours, setColours] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API_URL}/colours`).then((r) => setColours(r.data)).catch(() => setColours([]));
  }, []);

  const requestColour = (name) => {
    navigate(`/get-a-free-quote?colour=${encodeURIComponent(name)}`);
  };

  const straight = (colours || []).filter((c) => (c.style || "straight") === "straight");
  const herringbone = (colours || []).filter((c) => c.style === "herringbone");

  return (
    <div data-testid="colours-page">
      <PageHeader
        label="Flooring Colours"
        title="7.5 mm hybrid flooring colours"
        description="A selection of modern timber colours to suit Melbourne homes, in straight lay boards and herringbone. Every board is 7.5 mm thick with a rigid SPC core, pre-attached underlay and a deep embossed textured surface."
        testId="colours-header"
      />

      <div data-testid="colours-gallery">
        {colours === null ? (
          <p className="text-[#525252] max-w-7xl mx-auto px-6 py-20" data-testid="colours-loading">Loading colours…</p>
        ) : colours.length === 0 ? (
          <p className="text-[#525252] max-w-7xl mx-auto px-6 py-20" data-testid="colours-empty">Colour range coming soon — contact us to hear what is available.</p>
        ) : (
          <>
            <ColourSection
              label="Straight lay"
              title="Straight lay hybrid boards"
              description="Our standard 7.5 mm hybrid boards, laid in a classic straight pattern. The practical choice for most Melbourne homes."
              colours={straight}
              onRequest={requestColour}
              testId="colours-straight-section"
            />
            {herringbone.length > 0 && (
              <div className="bg-[#FAFAFA] border-y border-black/5">
                <ColourSection
                  label="Herringbone"
                  title="7.5 mm herringbone hybrid flooring"
                  description="The same durable 7.5 mm hybrid board laid in a herringbone pattern for a premium, feature-floor look. Great for living areas, hallways and entrances."
                  colours={herringbone}
                  onRequest={requestColour}
                  testId="colours-herringbone-section"
                />
              </div>
            )}
          </>
        )}
        <div className="max-w-7xl mx-auto px-6 pb-16">
          <p className="mt-4 text-sm text-[#525252] max-w-2xl leading-relaxed" data-testid="colours-note">
            Can&apos;t decide? Ask for samples or send us a photo of your space when you request a quote — we are
            happy to suggest colours that suit your home and lighting.
          </p>
        </div>
      </div>

      <CtaBand title="Found a colour you like?" text="Request it in your free quote and we will confirm pricing and availability for your floor area." />
    </div>
  );
}
